// Source: pv_soiling_questionnaire_fixed.xlsx (tabs "Questionnaire", "Scoring Logic", "Results Calculator", "Recommendations").

export interface SoilingType {
  key: string;
  label: string;
  recommendation: string;
}

export interface QuestionOption {
  value: string;
  label: string;
  // Points per soiling type, in SOILING_TYPES order.
  points: number[];
}

export interface Question {
  id: string;
  text: string;
  note?: string;
  options: QuestionOption[];
}

export interface QuestionSection {
  label: string;
  questions: Question[];
}

export const SOILING_TYPES: SoilingType[] = [
  {
    key: 'mineral_dust',
    label: 'Mineral Dust / Cementation',
    recommendation: `Based on the survey findings, the potential for dust cementation has been identified as a risk factor that may lead to additional energy losses. The following mitigation strategies are therefore recommended:
- Apply onsite monitoring: monitor PV soiling onsite to capture actual soiling rates (if not available, PM10/PM2.5 satellite data can be used).
- Soiling Mitigation Tool (SMT) can help you foresee cleaning actions with the right frequency (based on your actual soiling rates or PM data), to reach the economic optimum of your PV asset. Several cleaning strategies available in the tool (wet, dry, robotized, ...)

If cementation of dust is likely to appear on modules:
- Wet cleaning might be required periodically. In the SMT, it is possible to replace a dry cleaning campaign by a wet cleaning to simulate the economical impact on your PV asset.
- Consider periodic wet cleaning as a preventive strategy: We recommend evaluating cleaning strategies that include at least one wet cleaning campaign per year for locations where cementation occurs.`,
  },
  {
    key: 'pollen',
    label: 'Pollen',
    recommendation: `Based on the survey findings, the potential for pollen accumulation has been identified as a risk factor that may lead to additional energy losses. The following mitigation strategies are therefore recommended:

- Rely on natural cleaning whenever possible. In most cases, pollen is effectively removed by natural rainfall. Unless the site is exposed to exceptionally high airborne pollen concentrations combined with an unusually prolonged dry period, human cleaning is generally not required.

- If a human cleaning campaign has been foreseen on your PV asset due to other soiling sources than pollen, preferably schedule them just after the seasonal pollen peak in your region. Also consider that pollen production is highly seasonal and can vary in time and intensity from one year to another.

- Gentle cleaning with water or soft brushing is already enough to remove pollen from the surface of the PV module.`,
  },
  {
    key: 'lichen',
    label: 'Lichen',
    recommendation: `Based on the survey findings, the potential for lichen settlement has been identified as a risk factor that may lead to significant long-term energy losses. The following mitigation strategies are therefore recommended:

- Monitor the surrounding environment: The presence of lichen on rocks, tree trunks, branches, or other nearby surfaces should be considered an early alert sign and should trigger the implementation of a preventive cleaning strategy for the PV plant. Once established on the module surface, lichen does not disappear naturally, making early detection and prevention essential.

- Lichen development typically follows an exponential growth pattern, although the growth rate may vary from year to year depending on climatic conditions. An apparently slow development during the first years can rapidly evolve and significantly impact the energy production of the PV asset.

- Inspect the PV module surfaces at least every six months to assess the presence and development of lichen.

- Perform a gentle manual wet and/or mechanical cleaning every 2-3 years to prevent lichen settlement. When appropriate, mild biocides or dedicated anti-lichen agents may also be considered. Care should be taken to avoid dispersing spores, which can promote new settlements.

- If lichen is already established on the module surface, manual cleaning methods should be used, potentially combined with soft biocides or anti-lichen agents.

- Avoid spreading spores and lichen fragments during the cleaning process to minimize the risk of recolonization. Manual cleaning is recommended for this type of soiling; tractor-based or robotic cleaning solutions are generally not suitable.`,
  },
  {
    key: 'biofilm',
    label: 'Biofilm',
    recommendation: `Based on the survey findings, the potential for biofilm development has been identified as a risk factor that may lead to additional energy losses. The following mitigation strategies are therefore recommended:

- Perform periodic inspections at least every 6 months of the PV module surfaces to detect early signs of biofilm development and assess its progression over time.

- The Soiling Mitigation Tool (SMT) can help you define the optimal cleaning frequency based on your actual soiling rates or PM data, in order to achieve the economic optimum of your PV asset. Several cleaning strategies are available in the tool (wet, dry, robotized, ...). If the assessment result indicates that no cleaning is economically required, it is still recommended to consider a preventive cleaning campaign at least every 2 years to limit biofilm establishment and avoid the formation of persistent deposits.

- If biofilm is already present on the module surface, use appropriate cleaning methods combining controlled mechanical brushing with surfactants to effectively remove organic residues while minimizing the risk of damaging the module surface. EPS-degrading enzymes may be used as a pre-treatment before cleaning to break down the extracellular polymeric substances (EPS) matrix that contributes to biofilm adhesion and facilitate its removal.`,
  },
  {
    key: 'bird_droppings',
    label: 'Bird Droppings',
    recommendation: `Based on the survey findings, the potential for bird droppings accumulation has been identified as a risk factor that may lead to additional energy losses. The following mitigation strategies are therefore recommended:

- Monitor PV soiling onsite to capture actual soiling rates including rapid degradations due to bird droppings. Bird droppings create localized hard shading that can activate bypass diodes, generate hotspots, and significantly reduce the power output of affected modules.

- Install appropriate bird deterrent systems, such as physical barriers (e.g. spikes or nets), visual deterrents, or acoustic repellents, to reduce bird activity around the PV plant and minimize the recurrence of bird droppings on the modules.

- Use the Ex-Post Analysis available in the SMT to identify optimized cleaning schedules based on recent soiling data collected and to quantify losses during birds activity season.

- Periodically inspect the PV module surfaces to identify areas affected by bird droppings. Where available, camera-based monitoring and AI-assisted detection can be used to trigger cleaning actions based on visual evidence.

- For sites with frequent bird activity, consider increasing the cleaning frequency during high-risk periods.

Apply corrective cleaning when required:
- Bird droppings should preferably be rehydrated before removal to minimize the risk of scratching the module surface during mechanical cleaning. When necessary, use suitable surfactants or detergents to facilitate cleaning.`,
  },
  {
    key: 'industrial',
    label: 'Industrial / Hydrocarbon',
    recommendation: `Based on the survey findings, the potential for industrial soiling has been identified as a risk factor that may lead to significant energy losses. Hydrocarbon-based deposits such as black carbon (BC) and polycyclic aromatic hydrocarbons (PAHs), exhibit a much stronger optical and thermal impact than conventional mineral dust. Their sticky and hydrophobic nature promotes long-term accumulation on PV modules, increasing the persistence of soiling and potentially leading to cumulative energy yield losses if not properly managed. The following mitigation strategies are therefore recommended:

- If carbonaceous deposits are visibly accumulating on surrounding structures, equipment, or other exposed surfaces, consider installing an onsite PV soiling monitoring system to quantify the actual soiling rates. Industrial emissions are often not adequately represented by PM₁₀ and PM₂.₅ databases, making visual inspections and direct onsite measurements particularly important for this type of soiling.

- The Soiling Mitigation Tool (SMT) can help define the optimal cleaning frequency based on measured soiling rates. Because PM₁₀ and PM₂.₅ datasets may not accurately represent emissions from nearby industrial processes, cleaning optimization should preferably rely on onsite soiling measurements whenever available.

- Include water type cleaning methods to remove organic and carbonaceous films. When required, use suitable surfactants or solvents specifically designed for hydrocarbon-based deposits to restore the optical performance of the modules.`,
  },
];

export const QUESTION_SECTIONS: QuestionSection[] = [
  {
    label: 'Climate & Geography',
    questions: [
      {
        id: 'q1',
        text: 'What best describes the climate of the project site?',
        note: 'Determines baseline dust, biological and rain-cleaning conditions.',
        options: [
          { value: 'a', label: 'Arid / semi-arid / desert (rainfall <200 mm/yr)', points: [3, 0, 0, 0, 0, 0] },
          { value: 'b', label: 'Mediterranean / dry-summer temperate', points: [1, 1, 0, 0, 0, 0] },
          { value: 'c', label: 'Temperate / humid (rainfall >600 mm/yr, regular dew/fog)', points: [5, 3, 3, 3, 0, 0] },
          { value: 'd', label: 'Tropical / subtropical with high humidity and rainfall', points: [0, 1, 5, 5, 0, 0] },
        ],
      },
      {
        id: 'q2',
        text: 'What is the typical module tilt angle?',
        note: 'Low tilts increase accumulation of all soiling types.',
        options: [
          { value: 'a', label: '>30° or HSAT (steep, good drainage & self-cleaning)', points: [0, 0, 0, 0, 0, 0] },
          { value: 'b', label: '20°–30°', points: [3, 3, 1, 1, 3, 0] },
          { value: 'c', label: '<20° or flat (trackers at low angle)', points: [5, 5, 1, 1, 5, 0] },
        ],
      },
      {
        id: 'q3',
        text: 'Is morning dew, fog, or condensation frequent at the site?',
        note: 'Humidity triggers cementation and enables biological growth.',
        options: [
          { value: 'a', label: 'Rarely or never', points: [1, 0, 0, 0, 0, 0] },
          { value: 'b', label: 'Occasionally (a few times per month)', points: [3, 3, 3, 3, 0, 0] },
          { value: 'c', label: 'Frequently (several times per week)', points: [5, 5, 5, 5, 0, 0] },
        ],
      },
      {
        id: 'q4',
        text: 'What is the typical annual rainfall?',
        note: 'Rainfall >5–10 mm provides natural panel cleaning for dust; supports biological growth for higher values.',
        options: [
          { value: 'a', label: '<200 mm/yr (very low)', points: [5, 0, 0, 0, 0, 0] },
          { value: 'b', label: '200–800 mm/yr', points: [3, 1, 1, 1, 0, 0] },
          { value: 'c', label: '>800 mm/yr (high)', points: [0, 5, 3, 3, 0, 0] },
        ],
      },
    ],
  },
  {
    label: 'Mineral Dust & Cementation',
    questions: [
      {
        id: 'q6',
        text: 'Which option does represent the most significant local dust sources nearby?',
        options: [
          { value: 'a', label: 'Dust, sand, and bare soil particles', points: [5, 0, 0, 0, 0, 0] },
          { value: 'b', label: 'Seasonal agricultural work', points: [0, 5, 1, 1, 0, 0] },
          { value: 'c', label: 'Active roads of construction sites within ~5 km', points: [3, 0, 0, 0, 3, 3] },
          { value: 'd', label: 'Industrial facility, high-traffic road or port within <1 km', points: [3, 0, 0, 0, 5, 5] },
        ],
      },
      {
        id: 'q7',
        text: 'Do dust lines or crust deposits appear on module edges or surfaces after drying?',
        note: 'Visible cementation → reduced cleaning efficiency; assume ≥12% annual degradation if uncleaned.',
        options: [
          { value: 'a', label: 'No visible residue between cleans', points: [0, 0, 0, 0, 0, 0] },
          { value: 'b', label: 'Light haze, easily removed by rain', points: [1, 0, 0, 0, 0, 0] },
          { value: 'c', label: 'Visible dust lines; partial cementation observed', points: [3, 0, 0, 0, 0, 0] },
          { value: 'd', label: 'Hard crusted deposits; water or brushing required to remove', points: [5, 0, 0, 0, 0, 0] },
        ],
      },
    ],
  },
  {
    label: 'Biological Soiling (Pollen / Lichen / Biofilm)',
    questions: [
      {
        id: 'q8',
        text: 'What best describes the surrounding vegetation?',
        note: 'Determines pollen load, organic nutrient supply for biofilm/lichen.',
        options: [
          { value: 'a', label: 'No significant vegetation (urban, industrial, desert)', points: [3, 0, 0, 0, 0, 0] },
          { value: 'b', label: 'Sparse shrubs or grassland', points: [0, 1, 0, 0, 0, 0] },
          { value: 'c', label: 'Agricultural fields, orchards, or moderate grassland', points: [0, 3, 1, 1, 0, 0] },
          { value: 'd', label: 'Dense forests, woodland, or high-diversity rural ecosystem', points: [0, 5, 5, 5, 0, 0] },
        ],
      },
      {
        id: 'q9',
        text: 'Is there a defined pollen season with intense flowering nearby (within ~5 km)?',
        options: [
          { value: 'a', label: 'No – no significant flowering plants nearby', points: [0, 0, 0, 0, 0, 0] },
          { value: 'b', label: 'Low – scattered plants, mild season', points: [0, 1, 0, 0, 0, 0] },
          { value: 'c', label: 'Moderate – some trees/grasses with a clear spring season', points: [0, 3, 0, 0, 0, 0] },
          { value: 'd', label: 'High – dense tree canopy or extensive grass fields, heavy pollen season', points: [0, 5, 0, 0, 0, 0] },
        ],
      },
      {
        id: 'q10',
        text: 'What is the relative humidity pattern at the site?',
        note: 'RH >70% is the key trigger for biofilm adhesion and lichen activity.',
        options: [
          { value: 'a', label: 'Mainly dry (<40% RH)', points: [0, 0, 0, 0, 0, 0] },
          { value: 'b', label: 'Dry with occasional humid periods (40–60% RH)', points: [3, 0, 0, 0, 0, 0] },
          { value: 'c', label: 'Moderate, regularly >60% RH', points: [0, 0, 3, 3, 0, 0] },
          { value: 'd', label: 'High, frequently >70–80% RH (coastal, inland with fog)', points: [0, 0, 5, 5, 3, 0] },
        ],
      },
      {
        id: 'q11',
        text: 'Have lichens, biofilm, or algae been observed on modules, nearby structures, or hard surfaces at the site?',
        note: 'Direct evidence of biological colonisation risk.',
        options: [
          { value: 'a', label: 'Not observed anywhere', points: [0, 0, 0, 0, 0, 0] },
          { value: 'b', label: 'Present on rocks or trees only (natural surfaces)', points: [0, 0, 5, 3, 0, 0] },
          { value: 'c', label: 'Present on artificial surfaces (walls, fences, rooftops)', points: [0, 0, 5, 5, 0, 0] },
        ],
      },
    ],
  },
  {
    label: 'Bird Droppings',
    questions: [
      {
        id: 'q13',
        text: 'Is the site near coastlines, wetlands, landfills, migratory routes, or large agricultural areas?',
        options: [
          { value: 'a', label: 'No – urban/industrial site, far from bird attractors', points: [0, 0, 0, 0, 0, 0] },
          { value: 'b', label: 'Rural setting, moderate bird presence', points: [0, 0, 0, 0, 1, 0] },
          { value: 'c', label: 'Near water bodies, farmland, or moderate migratory path', points: [0, 0, 0, 0, 3, 0] },
          { value: 'd', label: 'On or adjacent to coast, landfill, wetland, or major migratory route', points: [0, 0, 0, 0, 5, 0] },
        ],
      },
      {
        id: 'q14',
        text: 'Are there elevated structures (cables, poles, module frames/edges) where birds perch?',
        options: [
          { value: 'a', label: 'Steep modules >30°, no perching points', points: [0, 0, 0, 0, 0, 0] },
          { value: 'b', label: 'Some moderate perching opportunities', points: [0, 0, 0, 0, 1, 0] },
          { value: 'c', label: 'Module frames or nearby structures regularly used by birds', points: [0, 0, 0, 0, 3, 0] },
          { value: 'd', label: 'Trackers, cables, and elevated frames – high perching activity observed', points: [0, 0, 0, 0, 5, 0] },
        ],
      },
      {
        id: 'q15',
        text: 'Have bird droppings been observed or documented at the site?',
        options: [
          { value: 'a', label: 'No evidence of bird presence', points: [0, 0, 0, 0, 0, 0] },
          { value: 'b', label: 'Occasional droppings, minimal coverage', points: [0, 0, 0, 0, 1, 0] },
          { value: 'c', label: 'Visible deposits on multiple modules, no production impact measured', points: [0, 0, 0, 0, 3, 0] },
          { value: 'd', label: 'Documented production losses >3% attributable to bird droppings', points: [0, 0, 0, 0, 5, 0] },
        ],
      },
    ],
  },
  {
    label: 'Industrial / Hydrocarbon Soiling',
    questions: [
      {
        id: 'q16',
        text: 'How close is the site to heavy industrial emitters (refineries, chemical plants, metallurgy, ports, high-traffic roads)?',
        options: [
          { value: 'a', label: '>30 km, no relevant sources', points: [0, 0, 0, 0, 0, 0] },
          { value: 'b', label: '15–30 km from moderate sources', points: [0, 0, 0, 0, 0, 1] },
          { value: 'c', label: '5–15 km from significant sources or heavy traffic corridor', points: [0, 0, 0, 0, 0, 3] },
          { value: 'd', label: '<5 km from industrial pole or continuous heavy traffic', points: [0, 0, 0, 0, 0, 5] },
        ],
      },
      {
        id: 'q17',
        text: 'What type of deposit has been observed on modules or nearby surfaces?',
        options: [
          { value: 'a', label: 'No particular deposit / only loose mineral dust', points: [3, 0, 0, 0, 0, 0] },
          { value: 'b', label: 'Occasional mixed mineral-organic film', points: [0, 1, 3, 3, 0, 0] },
          {
            value: 'c',
            label:
              'Recurrent dark or greasy film on surfaces OR persistent hydrocarbon/carbonaceous deposit (PAH/black carbon), very adherent',
            points: [0, 0, 0, 0, 0, 5],
          },
        ],
      },
    ],
  },
  {
    label: 'O&M & Site History',
    questions: [
      {
        id: 'q21',
        text: 'Is the site a rooftop installation?',
        note: 'Rooftops concentrate exhaust, biological debris and gull traffic.',
        options: [
          { value: 'a', label: 'No – ground-mounted open field', points: [1, 0, 0, 0, 0, 0] },
          { value: 'b', label: 'No – but near rooftops / urban surfaces', points: [0, 0, 0, 0, 1, 1] },
          { value: 'c', label: 'Yes – industrial or commercial rooftop', points: [0, 0, 0, 0, 3, 3] },
          { value: 'd', label: 'Yes – in urban area with significant rooftop pollution', points: [0, 0, 0, 0, 5, 5] },
        ],
      },
      {
        id: 'q22',
        text: 'What is the distance from the coast or large water body?',
        note: 'Coastal proximity increases bird droppings, salt and humidity effects.',
        options: [
          { value: 'a', label: '>50 km inland', points: [0, 0, 0, 0, 0, 0] },
          { value: 'b', label: '20–50 km', points: [0, 0, 0, 0, 1, 0] },
          { value: 'c', label: '5–20 km', points: [0, 0, 0, 3, 3, 0] },
          { value: 'd', label: '<5 km (marine environment)', points: [3, 0, 3, 5, 5, 0] },
        ],
      },
    ],
  },
];

export const QUESTIONS = QUESTION_SECTIONS.flatMap((section) => section.questions);

// "Soiling Risk Detected" threshold on the 0-100 score (Results Calculator, rows 25-27).
const RISK_DETECTED_SCORE = 75;

export const getDetectedSoilingTypes = (answers: Record<string, string | null | undefined>): SoilingType[] =>
  SOILING_TYPES.filter((_, typeIndex) => {
    const max = QUESTIONS.reduce((sum, q) => sum + Math.max(...q.options.map((o) => o.points[typeIndex])), 0);
    const raw = QUESTIONS.reduce((sum, q) => sum + (q.options.find((o) => o.value === answers[q.id])?.points[typeIndex] ?? 0), 0);
    return max > 0 && Math.round((raw / max) * 1000) / 10 >= RISK_DETECTED_SCORE;
  });
